import React from 'react';

interface ProgressRingProps {
  /** Progress value 0-100 */
  value: number;
  /** Ring size in pixels */
  size?: number;
  /** Stroke width */
  strokeWidth?: number;
  /** Color of the progress ring */
  color?: string;
  /** Background track color */
  trackColor?: string;
  /** Show percentage text in the center */
  showValue?: boolean;
  /** Custom center label */
  label?: string;
  /** Custom center content */
  children?: React.ReactNode;
  /** Animate the ring fill */
  animate?: boolean;
}

/**
 * ProgressRing — SVG circular progress indicator with smooth animation.
 */
export default function ProgressRing({
  value,
  size = 120,
  strokeWidth = 8,
  color = '#7c5cfc',
  trackColor = '#0f3460',
  showValue = true,
  label,
  children,
  animate = true,
}: ProgressRingProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedValue = Math.max(0, Math.min(100, value));
  const offset = circumference - (clampedValue / 100) * circumference;
  const center = size / 2;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
          opacity={0.4}
        />
        {/* Progress arc */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: animate ? 'stroke-dashoffset 0.8s ease-out, stroke 0.3s ease' : 'none',
            ['--target-offset' as string]: offset,
          }}
        />
      </svg>
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children ? (
          children
        ) : (
          <>
            {showValue && (
              <span className="text-2xl font-bold text-txt-primary font-mono">
                {Math.round(clampedValue)}%
              </span>
            )}
            {label && (
              <span className="text-xs text-txt-muted font-sans mt-0.5">{label}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
